import React, { useEffect, useState } from "react";
import moment from "moment";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import { ActivityGuideLink } from "utility/ActivityGuides";
import { MotionRomTypeMap, romBadgeColor, romBadgeTextColor, romBarColor, romLevel, romNormalizedValue, romTooltip } from "utility/RomUtils";
import { classNames } from "utility/Utils";
import {
    CUSTOM_EVENT_FORMATTERS,
    EVENT_FORMATTERS,
    UNEXPECTED_ACTIVITY_EVENTS,
    getActivityDefinition,
    UNEXPECTED_GOAL_LABEL_MAP
} from "sessions/SessionActivityDefinitions";
import { scoreBadgeBackgroundColor, scoreBarColor, scoreEncouragement, scoreTextColor, scoreTooltip } from "planEditor/ScoreUtils";

const SessionBonusPanel = ({ activity, routine = false }) => {
    const [additionalEventsOpened, setAdditionalEventsOpened] = useState(false);
    const [opened, setOpened] = useState(routine);
    const [dontRender, setDontRender] = useState(false);

    // ---=== Utilities ===--- //

    const parseCamelCaseTitle = (title) => {
        const result = title.replace(/([A-Z])/g, " $1");
        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    const timeConvert = (raw) => {
        let times = raw.split(":");
        let hours = times[0];
        let minutes = times[1];
        let AmOrPm = hours >= 12 ? "pm" : "am";
        hours = (hours % 12) || 12;
        return hours + ":" + minutes + AmOrPm;
    };

    const showActivityGuide = () => (
        <ActivityGuideLink activityId={activity.id} buttonSize={30} iconSize={18} />
    );

    const getActivityBadge = (type, rank) => {
        if (rank === null) return `/icons/badges/${type}Activity_None.png`;
        const level = rank === 1 ? "Bronze" : rank === 2 ? "Silver" : "Gold";
        return `/icons/badges/${type}Activity_${level}.png`;
    };

    const cleanLabel = (label) => {
        // Updated to prevent clipping and allow the grid to resize.
        return <div className="text-gray-700 text-sm font-medium leading-5 whitespace-nowrap">{label}</div>;
    };

    // ---=== Data Checks ===--- //

    const hasRoms = (roms) => roms && Object.keys(roms).length > 0;
    const hasGoals = (goals) => goals && Object.keys(goals).length > 0;
    const hasEvents = (events) => events && events.length > 0;

    // ---=== useEffects ===--- //

    useEffect(() => {
        if (!activity) {
            setDontRender(true);
            return;
        }
        const activityDef = getActivityDefinition(activity.id);
        const hasAnyData
            = activity.motion_time !== undefined
            || hasRoms(activity.roms)
            || hasGoals(activity.scores?.goals)
            || hasEvents(activity.events)
            || (activityDef?.expectedRoms?.length ?? 0) > 0
            || (activityDef?.expectedGoals?.length ?? 0) > 0
            || (activityDef?.expectedEvents?.length ?? 0) > 0;
        setDontRender(!hasAnyData);
    }, [activity]);

    // ---=== Render Utilities ===--- //

    const weeklyBestDecorator = () => (
        <div className="mix-blend-multiply justify-start items-start inline-flex">
            <div className="pl-1.5 pr-2 py-0.5 bg-brand-50 rounded-2xl justify-center items-center gap-1 flex">
                <img className="w-3 h-3 relative" src="/icons/award-blue.svg" alt="icon" />
                <div className="text-center text-brand-700 text-xs font-medium leading-[18px]">Weekly Best</div>
            </div>
        </div>
    );

    const allTimeBestDecorator = () => (
        <div className="mix-blend-multiply justify-start items-start inline-flex">
            <div className="pl-1.5 pr-2 py-0.5 bg-brand-50 rounded-2xl justify-center items-center gap-1 flex">
                <img className="w-3 h-3 relative" src="/icons/award-blue.svg" alt="icon" />
                <div className="text-center text-brand-700 text-xs font-medium leading-[18px]">All-Time Best</div>
            </div>
        </div>
    );

    // ---=== Render Completion ===--- //

    const renderCompleted = (completed) => {
        if (completed) {
            return (
                <div className="mix-blend-multiply justify-start items-start flex">
                    <div className="pl-1.5 pr-2 py-0.5 bg-success-50 rounded-2xl justify-center items-center gap-1.5 flex">
                        <div className="w-2 h-2 relative"><div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-success-500 rounded-full" /></div>
                        <div className="text-center text-success-700 text-xs font-medium leading-[18px]">Completed</div>
                    </div>
                </div>
            );
        }

        let statusText = "Incomplete", bgColor = "bg-warning-50", dotColor = "bg-warning-500", textColor = "text-warning-700";
        if (activity.restarted) { statusText = "Restarted"; bgColor = "bg-error-100"; dotColor = "bg-error-500"; textColor = "text-error-700"; }
        else if (activity.timed_out) { statusText = "Timed Out"; bgColor = "bg-error-100"; dotColor = "bg-error-500"; textColor = "text-error-700"; }
        else if (activity.skipped) { statusText = "Skipped"; bgColor = "bg-error-100"; dotColor = "bg-error-500"; textColor = "text-error-700"; }

        return (
            <div className="mix-blend-multiply justify-start items-start flex">
                <div className={`pl-1.5 pr-2 py-0.5 ${bgColor} rounded-2xl justify-center items-center gap-1.5 flex`}>
                    <div className="w-2 h-2 relative"><div className={`w-1.5 h-1.5 left-[1px] top-[1px] absolute ${dotColor} rounded-full`} /></div>
                    <div className={`text-center ${textColor} text-xs font-medium leading-[18px]`}>{statusText}</div>
                </div>
            </div>
        );
    };

    // ---=== Render Score ===--- //

    const renderScoreRow = (score) => (
        <React.Fragment key="score">
            <div className="flex items-center gap-2"><img className="w-5 h-5" src="/icons/score.svg" alt="icon" />{cleanLabel("Score")}</div>
            <div className="min-w-0">
                <div className="flex items-center gap-4 w-full">
                    <div className="grow shrink relative h-5 flex items-center">
                        <div className="w-full absolute h-2 bg-gray-200 rounded-full" />
                        <div className={classNames("h-2 absolute rounded-full", scoreBarColor(score))} style={{ width: `${Math.round((score / 5000) * 100)}%` }} />
                    </div>
                    <span className="inline-flex gap-2 text-sm items-center">
                        {score}
                        <div className={`px-2 py-0.5 has-tooltip relative rounded-2xl justify-center items-center flex ${scoreBadgeBackgroundColor(score)}`}>
                            <span className="tooltip rounded shadow-lg p-1 -mt-12">{scoreTooltip(score)}</span>
                            <div className={`text-center hs-tooltip text-xs font-medium leading-[18px] text-nowrap ${scoreTextColor(score)}`}>{scoreEncouragement(score)}</div>
                        </div>
                        {activity.scores?.top_alltime && allTimeBestDecorator()}
                        {activity.scores?.top_weekly && weeklyBestDecorator()}
                    </span>
                </div>
            </div>
        </React.Fragment>
    );

    // ---=== Render ROMs ===--- //

    const renderRomRow = (key, romLabel, romMeasureType, romValue) => {
        const labelContent = cleanLabel(romLabel);

        return (
            <React.Fragment key={key}>
                <div className="flex items-center gap-2">
                    <img className="w-5 h-5 flex-shrink-0" src="/icons/compass.svg" alt="icon" />
                    {labelContent}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-4 w-full">
                        <div className="grow shrink relative h-5 flex items-center">
                            <div className="w-full absolute h-2 bg-gray-200 rounded-full" />
                            <div className={classNames("h-2 absolute rounded-full", romBarColor(romMeasureType, romValue))} style={{ width: `${romNormalizedValue(romMeasureType, romValue)}%` }} />
                        </div>
                        <span className="inline-flex gap-2 text-sm items-center">
                            {`${Math.round(romValue)}°`}
                            <div className={`px-2 py-0.5 has-tooltip relative rounded-2xl justify-center items-center flex ${romBadgeColor(romMeasureType, romValue)}`}>
                                <span className="tooltip rounded shadow-lg p-1 -mt-12">{romTooltip(romMeasureType, romValue)}</span>
                                <div className={`text-center hs-tooltip text-xs font-medium leading-[18px] text-nowrap ${romBadgeTextColor(romMeasureType, romValue)}`}>{romLevel(romMeasureType, romValue)}</div>
                            </div>
                        </span>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderCombinedRoms = () => {
        const activityDef = getActivityDefinition(activity.id);
        const expectedRoms = activityDef?.expectedRoms || [];
        const actualRoms = activity.roms || {};
        const renderedKeys = new Set();
        const elements = [];

        // 1. Render all expected ROMs, with data if present
        expectedRoms.forEach(expectedRom => {
            const actualRomValue = actualRoms[expectedRom.key];
            if (actualRomValue != null && MotionRomTypeMap[expectedRom.key]) {
                const romRow = renderRomRow(expectedRom.key, expectedRom.label, MotionRomTypeMap[expectedRom.key], actualRomValue);
                const children = React.Children.toArray(romRow.props.children);
                elements.push(
                    <React.Fragment key={expectedRom.key}>
                        {children[0]}
                        {children[1]}
                    </React.Fragment>
                );
            }
            else {
                elements.push(
                    <React.Fragment key={expectedRom.key}>
                        <div className="flex items-center gap-2"><img className="w-5 h-5" src="/icons/compass.svg" alt="icon" />{cleanLabel(expectedRom.label)}</div>
                        <div className="flex items-center text-gray-700 text-sm font-medium">-</div>
                    </React.Fragment>
                );
            }
            renderedKeys.add(expectedRom.key);
        });

        // 2. Render actual ROMs that were not in the expected list
        Object.keys(actualRoms).forEach(romKey => {
            if (!renderedKeys.has(romKey) && MotionRomTypeMap[romKey]) {
                const romRow = renderRomRow(romKey, parseCamelCaseTitle(romKey), MotionRomTypeMap[romKey], actualRoms[romKey]);
                const children = React.Children.toArray(romRow.props.children);
                elements.push(
                    <React.Fragment key={romKey}>
                        {children[0]}
                        {children[1]}
                    </React.Fragment>
                );
            }
        });

        return elements;
    };

    // ---=== Render Motion Time ===--- //

    const renderMotionTimeRow = () => {
        const duration = (activity.motion_time ? parseFloat(activity.motion_time) : 0) * 1000;
        const timeString = duration > 0 ? `${Math.floor(moment.duration(duration).asHours()) > 0 ? `${Math.floor(moment.duration(duration).asHours())}h ` : ""}${moment.duration(duration).minutes() ? `${moment.duration(duration).minutes()}m ` : ""} ${moment.duration(duration).seconds() > 0 ? `${moment.duration(duration).seconds()}s` : ""}`.trim() : "-";
        return (
            <React.Fragment key="motion-time">
                <div className="flex items-center gap-2"><img className="w-5 h-5" src="/icons/clock-refresh.svg" alt="icon" />{cleanLabel("Motion Time")}</div>
                <div className="min-w-0">
                    <div className="flex items-center text-gray-700 text-sm font-medium leading-5">{timeString || "0s"}</div>
                </div>
            </React.Fragment>
        );
    };

    // ---=== Render Goals ===--- //

    const renderGoalRow = (key, label, goals, alltime = false, weekly = false, showIcon = true) => (
        <React.Fragment key={key}>
            <div className={`flex items-center gap-2 ${!showIcon ? "pl-7" : ""}`}>
                {showIcon && <img className="w-5 h-5" src="/icons/target.svg" alt="icon" />}
                {cleanLabel(label)}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <span>{goals}</span>
                    {alltime && allTimeBestDecorator()}
                    {weekly && weeklyBestDecorator()}
                </div>
            </div>
        </React.Fragment>
    );

    const renderCombinedGoals = () => {
        const activityDef = getActivityDefinition(activity.id);
        const expectedGoals = activityDef?.expectedGoals || [];
        const actualGoals = activity.scores?.goals || [];

        const actualGoalsMap = new Map();
        actualGoals.forEach(goal => actualGoalsMap.set(goal.label, goal));

        const expectedElements = [];
        const unexpectedGoalData = [];
        const handledActualGoalKeys = new Set();

        expectedGoals.forEach(expected => {
            const actualGoal = actualGoalsMap.get(expected.key);
            if (actualGoal) {
                expectedElements.push(renderGoalRow(expected.key, expected.label, actualGoal.completions, actualGoal.top_alltime, actualGoal.top_weekly));
                handledActualGoalKeys.add(expected.key);
            }
            else {
                expectedElements.push(renderGoalRow(expected.key, expected.label, "-", false, false));
            }
        });

        actualGoalsMap.forEach((actualGoal, key) => {
            if (!handledActualGoalKeys.has(key)) {
                const label = UNEXPECTED_GOAL_LABEL_MAP[key] || actualGoal.label;
                unexpectedGoalData.push({ ...actualGoal, key: key, displayLabel: label });
            }
        });

        const finalElements = [...expectedElements];
        if (unexpectedGoalData.length > 0) {
            const unexpectedElements = [
                renderGoalRow("additional-goals-header", "Additional Goals", "", false, false, true),
                ...unexpectedGoalData.map(goal =>
                    renderGoalRow(goal.key, goal.displayLabel, goal.completions, goal.top_alltime, goal.top_weekly, false)
                )
            ];
            finalElements.push(
                <div key="additional-goals-wrapper" className="col-span-2 grid grid-cols-subgrid gap-x-4 gap-y-2.5 bg-gray-50 rounded-lg -mx-1 px-1">
                    {unexpectedElements}
                </div>
            );
        }

        return finalElements;
    };

    // ---=== Render Events ===--- //

    const renderEventRow = (key, label, value, showIcon = true) => (
        <React.Fragment key={key}>
            <div className={`flex items-center gap-2 ${!showIcon ? "pl-7" : ""}`}>
                {showIcon && <img className="w-5 h-5" src="/icons/info.svg" alt="icon" />}
                {cleanLabel(label)}
            </div>
            <div className="min-w-0">
                {
                    React.isValidElement(value)
                        ? (
                            value
                        )
                        : (
                            <div className="flex items-center text-gray-700 text-sm font-medium leading-5">{String(value)}</div>
                        )
                }
            </div>
        </React.Fragment>
    );

    const renderCombinedEvents = () => {
        const activityDef = getActivityDefinition(activity.id);
        const expectedEvents = activityDef?.expectedEvents || [];
        const actualEvents = activity.events || [];
        const ignoredEvents = activityDef?.ignoredEvents || [];

        const actualEventsByType = new Map<string, any[]>();
        const filteredActualEvents = actualEvents.filter(e => !ignoredEvents.includes(e.type));

        filteredActualEvents.forEach(e => {
            if (!actualEventsByType.has(e.type)) {
                actualEventsByType.set(e.type, []);
            }
            const eventList = actualEventsByType.get(e.type);
            if (eventList) {
                eventList.push(e);
            }
        });

        const expectedElements = [];
        const unexpectedEventData = [];
        const handledActualKeys = new Set();

        expectedEvents.forEach(expected => {
            const relevantEvents = actualEventsByType.get(expected.key) || [];
            let value;

            if (relevantEvents.length > 0) {
                const formatKey = expected.format;
                const formatter = formatKey ? (CUSTOM_EVENT_FORMATTERS[formatKey] || EVENT_FORMATTERS[formatKey]) : null;

                if (formatter) {
                    value = formatter(relevantEvents);
                }
                else {
                    const rawValue = relevantEvents[0].value;
                    value = (rawValue === null || rawValue === undefined) ? "-" : String(rawValue);
                }
                handledActualKeys.add(expected.key);
            }
            else {
                value = "-";
            }
            expectedElements.push(renderEventRow(expected.key, expected.label, value));
        });

        actualEventsByType.forEach((events, key) => {
            if (!handledActualKeys.has(key) && !ignoredEvents.includes(key)) {
                const unexpectedDef = UNEXPECTED_ACTIVITY_EVENTS.find(e => e.key === key);
                const label = unexpectedDef?.label || parseCamelCaseTitle(key.split("__").pop());
                let value;

                const formatKey = unexpectedDef?.format;
                const formatter = formatKey ? (CUSTOM_EVENT_FORMATTERS[formatKey] || EVENT_FORMATTERS[formatKey]) : null;

                if (formatter) {
                    value = formatter(events);
                }
                else {
                    const rawValue = events.length > 0 ? events[0].value : null;
                    value = (rawValue === null || rawValue === undefined) ? "-" : String(rawValue);
                }

                unexpectedEventData.push({ key, label, value });
            }
        });

        const finalElements = [...expectedElements];
        if (unexpectedEventData.length > 0) {
            const header = (
                <React.Fragment key="additional-events-header">
                    <div className="flex items-center gap-2">
                        <img className="w-5 h-5" src="/icons/info.svg" alt="icon" />
                        {cleanLabel("Additional Events")}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center text-gray-700 text-sm font-medium leading-5">
                            {additionalEventsOpened ? <ChevronUpIcon className="h-5 w-5 text-gray-700" /> : <ChevronDownIcon className="h-5 w-5 text-gray-700" />}
                        </div>
                    </div>
                </React.Fragment>
            );

            const unexpectedElements = unexpectedEventData.map(event =>
                renderEventRow(event.key, event.label, event.value, false)
            );

            finalElements.push(
                <div
                    key="additional-events-wrapper"
                    className="col-span-2 grid grid-cols-subgrid gap-x-4 gap-y-2.5 bg-gray-50 rounded-lg -mx-1 px-1 cursor-pointer"
                    onClick={
                        (e) => {
                            e.stopPropagation();
                            setAdditionalEventsOpened(prev => !prev);
                        }
                    }
                >
                    {header}
                    {additionalEventsOpened && unexpectedElements}
                </div>
            );
        }

        return finalElements;
    };

    // ---=== Render ===--- //

    const renderDataSections = () => {
        const sections = [];

        if (activity.type === "Movement") {
            sections.push(renderScoreRow(activity.scores?.score ?? 0));
        }

        const roms = renderCombinedRoms();
        if (roms.length > 0) sections.push(...roms);

        sections.push(renderMotionTimeRow());

        const goals = renderCombinedGoals();
        if (goals.length > 0) sections.push(...goals);

        const events = renderCombinedEvents();
        if (events.length > 0) sections.push(...events);

        return sections;
    };

    if (dontRender) {
        return null;
    }

    return (
        <div className="w-full flex justify-start items-center gap-5">
            <div className="self-stretch flex-col justify-start items-end gap-2.5 inline-flex">
                <div className="text-right text-gray-500 text-sm font-medium leading-tight">{activity.start_time ? timeConvert(activity.start_time) : "n/a"}</div>
                <div className="w-px grow shrink basis-0 bg-gray-400" />
                <div className="text-right text-gray-500 text-sm font-medium leading-tight">{activity.end_time ? timeConvert(activity.end_time) : "n/a"}</div>
            </div>
            <div className="grow shrink-0 basis-auto px-6 py-[15px] rounded-[10px] shadow border border-gray-200 flex flex-col justify-start items-start gap-[15px] cursor-pointer" onClick={() => setOpened(prev => !prev)}>
                <div className="self-stretch flex justify-start items-center gap-[15px] w-full">
                    <div className="text-gray-900 text-xl font-semibold leading-[30px]">{activity.name}</div>
                    <img className="w-7 h-7" src={getActivityBadge(activity.type, activity.completion_level)} alt="badge" />
                    {renderCompleted(activity.completed)}
                    <div className="flex-grow" />
                    <div onClick={(e) => e.stopPropagation()}>
                        {showActivityGuide()}
                    </div>
                    {opened ? <ChevronUpIcon className="h-5 w-5 text-gray-700" /> : <ChevronDownIcon className="h-5 w-5 text-gray-700" />}
                </div>
                {
                    opened && (
                        <div className="grid w-full gap-x-4 gap-y-2.5 grid-cols-[auto_1fr] pt-2.5">
                            {renderDataSections()}
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default SessionBonusPanel;
